import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Play, Image, Eye, Heart, Crown, Lock, X,
  Sparkles, Users, Utensils, MessageCircle, HeartHandshake, 
  Phone, XCircle, Star, MapPin, ExternalLink, Building2,
  Mail, User, Send, CheckCircle, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { FooterSection } from "../components/landing/FooterSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryInfo = {
  first_impression: { 
    title: "첫인상 매너", 
    icon: Users, 
    color: "rose",
    description: "첫 만남에서 좋은 인상을 남기는 방법"
  },
  date: { 
    title: "데이트 매너", 
    icon: HeartHandshake, 
    color: "burgundy",
    description: "성공적인 데이트를 위한 에티켓"
  },
  dining: { 
    title: "식사 매너", 
    icon: Utensils, 
    color: "gold",
    description: "품격있는 식사 예절"
  },
  conversation: { 
    title: "대화 매너", 
    icon: MessageCircle, 
    color: "teal",
    description: "매력적인 대화의 기술"
  },
  emotion: { 
    title: "감정 매너", 
    icon: Heart, 
    color: "pink",
    description: "감정 표현과 공감의 기술"
  },
  contact: { 
    title: "연락 매너", 
    icon: Phone, 
    color: "blue",
    description: "적절한 연락의 타이밍과 방법"
  },
  rejection: { 
    title: "거절 매너", 
    icon: XCircle, 
    color: "stone",
    description: "상처주지 않는 거절의 기술"
  },
};

export default function MannerClassPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  const [contents, setContents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");

  // URL 파라미터가 바뀌면 카테고리 자동 업데이트
  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setSelectedCategory(cat);
  }, [searchParams]);

  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAdInquiryModal, setShowAdInquiryModal] = useState(false);
  const [adInquirySubmitting, setAdInquirySubmitting] = useState(false);
  const [adInquirySuccess, setAdInquirySuccess] = useState(false);
  const [adInquiryForm, setAdInquiryForm] = useState({
    company_name: "",
    category: "first_impression",
    contact_name: "",
    phone: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    fetchContents();
    fetchSponsors();
  }, [selectedCategory]);

  const fetchContents = async () => {
    setIsLoading(true);
    try {
      const url = selectedCategory === "all" 
        ? `${API_URL}/api/manner-class/contents`
        : `${API_URL}/api/manner-class/contents?category=${selectedCategory}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setContents(data.contents || []);
    } catch (error) {
      console.error("Error fetching contents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      const url = selectedCategory === "all" 
        ? `${API_URL}/api/manner-class/sponsors`
        : `${API_URL}/api/manner-class/sponsors?category=${selectedCategory}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setSponsors(data.sponsors || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  const handleContentClick = (content) => {
    if (content.is_premium && (!user || user.membership_type === "basic")) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedContent(content);
  };

  const handleAdInquirySubmit = async (e) => {
    e.preventDefault();
    setAdInquirySubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAdInquirySuccess(true);
      setAdInquiryForm({
        company_name: "",
        category: "first_impression",
        contact_name: "",
        phone: "",
        email: "",
        message: "",
      });
      
      setTimeout(() => {
        setShowAdInquiryModal(false);
        setAdInquirySuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      alert("문의 접수 중 오류가 발생했습니다.");
    } finally {
      setAdInquirySubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-stone-200" data-testid="manner-class-page">
      {/* Hero Section with Background */}
      <section className="pt-24 pb-12 bg-stone-200">
        <div className="max-w-[95%] mx-auto">
          <div className="relative rounded-3xl overflow-hidden shadow-lg min-h-[400px] md:min-h-[450px]">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-contain bg-right bg-no-repeat"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_c4b49081-8dac-42ca-9595-689c86b24925/artifacts/nm4071yn_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2001_49_48%20PM.png')`,
                backgroundColor: '#f5f5f4'
              }}
            />
            
            {/* Overlay gradient for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/50 to-transparent" />
            
            {/* Content */}
            <div className="relative z-10 p-8 md:p-12 flex flex-col justify-center h-full min-h-[400px] md:min-h-[450px]">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-gold-300 hover:text-gold-200 mb-6 transition-colors w-fit"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>홈으로 돌아가기</span>
              </button>

              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gold-500/30 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-gold-400" />
                  </div>
                  <span className="text-gold-400 text-sm font-medium tracking-wider uppercase">
                    Manner Class
                  </span>
                </div>

                <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl font-[720] text-white mb-6">
                  매너 클래스
                </h1>
                
                <p className="text-white/90 text-lg md:text-xl leading-relaxed">
                  진정한 매력은 매너에서 시작됩니다.
                  <br className="hidden md:block" />
                  품격있는 만남을 위한 에티켓을 배워보세요.
                </p>
                <p className="text-white/70 text-base md:text-lg mt-3">
                  첫인상부터 대화, 식사 예절까지
                  <br className="hidden md:block" />
                  관계의 모든 순간을 아름답게 만드는 매너의 정석
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-8 bg-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === "all"
                  ? "bg-burgundy-500 text-white shadow-lg"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              전체
            </button>
            {Object.entries(categoryInfo).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-5 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === key
                    ? "bg-burgundy-500 text-white shadow-lg"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                <info.icon className="w-4 h-4" />
                {info.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Grid */}
      <section className="py-8 pb-16 bg-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-burgundy-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : contents.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">아직 등록된 콘텐츠가 없습니다.</p>
              <p className="text-stone-400 text-sm mt-2">곧 유익한 매너 콘텐츠가 업로드될 예정입니다!</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {contents.map((content) => {
                const category = categoryInfo[content.category] || categoryInfo.first_impression;
                const isPremiumLocked = content.is_premium && (!user || user.membership_type === "basic");
                
                return (
                  <motion.div
                    key={content.id}
                    variants={itemVariants}
                    onClick={() => handleContentClick(content)}
                    className={`group bg-stone-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer ${
                      isPremiumLocked ? "opacity-90" : ""
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-stone-200 overflow-hidden">
                      {content.thumbnail_url ? (
                        <img
                          src={content.thumbnail_url.startsWith("http") ? content.thumbnail_url : `${API_URL}${content.thumbnail_url}`}
                          alt={content.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-burgundy-100 to-burgundy-200">
                          <category.icon className="w-12 h-12 text-burgundy-400" />
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Content type badge */}
                      <div className="absolute top-3 left-3">
                        {content.content_type === "video" ? (
                          <span className="px-3 py-1 bg-burgundy-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <Play className="w-3 h-3" fill="white" />
                            영상
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gold-500 text-stone-900 text-xs font-medium rounded-full flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            아티클
                          </span>
                        )}
                      </div>

                      {/* Premium badge */}
                      {content.is_premium && (
                        <div className="absolute top-3 right-3">
                          <span className="px-3 py-1 bg-gradient-to-r from-gold-500 to-gold-600 text-stone-900 text-xs font-bold rounded-full flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            프리미엄
                          </span>
                        </div>
                      )}

                      {/* Lock overlay for premium content */}
                      {isPremiumLocked && (
                        <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center">
                          <div className="text-center text-white">
                            <Lock className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-sm">정회원 전용</p>
                          </div>
                        </div>
                      )}

                      {/* Category & Stats */}
                      <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center">
                        <span className="text-white text-sm font-medium">
                          {category.title}
                        </span>
                        <div className="flex items-center gap-3 text-white/80 text-xs">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {content.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {content.likes || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-stone-800 text-lg mb-2 line-clamp-1 group-hover:text-burgundy-600 transition-colors">
                        {content.title}
                      </h3>
                      <p className="text-stone-500 text-sm line-clamp-2">
                        {content.description}
                      </p>
                      
                      {/* Tags */}
                      {content.tags && content.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {content.tags.slice(0, 3).map((tag, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-stone-200 text-stone-600 text-xs rounded-full"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Sponsors Section */}
      {sponsors.length > 0 && (
        <section className="py-12 bg-stone-100">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-burgundy-500" />
                <span className="text-burgundy-500 font-medium text-sm tracking-wider uppercase">
                  Recommended Partners
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-sans font-[720] text-stone-800">
                {selectedCategory === "all" ? "추천 강사 & 업체" : `${categoryInfo[selectedCategory]?.title || ""} 추천`}
              </h2>
              <p className="text-stone-500 mt-2">
                New Coupling이 엄선한 매너 전문가들입니다
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sponsors.map((sponsor, index) => (
                <motion.div
                  key={sponsor.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group border border-stone-200"
                >
                  <div className="relative h-48 bg-gradient-to-br from-burgundy-400 to-burgundy-600 overflow-hidden">
                    {sponsor.image_url ? (
                      <img
                        src={sponsor.image_url.startsWith("http") ? sponsor.image_url : `${API_URL}${sponsor.image_url}`}
                        alt={sponsor.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-white/30" />
                      </div>
                    )}
                    
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-gold-500 text-stone-900 text-xs font-bold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        추천
                      </span>
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-bold text-lg">{sponsor.title}</h3>
                      {sponsor.location && (
                        <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {sponsor.location}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <p className="text-stone-600 text-sm line-clamp-2 mb-4">
                      {sponsor.description}
                    </p>

                    <div className="flex items-center justify-between">
                      {sponsor.contact && (
                        <a 
                          href={`tel:${sponsor.contact}`}
                          className="flex items-center gap-1 text-stone-500 hover:text-burgundy-500 text-sm transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                          {sponsor.contact}
                        </a>
                      )}
                      
                      {sponsor.link_url && (
                        <a
                          href={sponsor.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-4 py-2 bg-burgundy-500 hover:bg-burgundy-600 text-white text-sm font-medium rounded-full transition-colors"
                        >
                          방문하기
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Ad Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-br from-stone-100 via-stone-200 to-burgundy-100/50 rounded-2xl p-6 md:p-8 text-center border border-stone-300"
            >
              <p className="text-burgundy-500 text-sm font-medium mb-2">강사 등록 문의</p>
              <h3 className="text-stone-700 text-xl font-bold mb-2">
                매너 강사로 등록하세요
              </h3>
              <p className="text-stone-500 text-sm mb-4">
                New Coupling 회원들에게 매너 강의를 제공하고 새로운 수강생을 만나보세요
              </p>
              <Button
                onClick={() => setShowAdInquiryModal(true)}
                variant="outline"
                className="border-burgundy-400 text-burgundy-500 hover:bg-burgundy-500 hover:text-white"
              >
                등록 문의하기
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* If no sponsors, show ad banner only */}
      {sponsors.length === 0 && (
        <section className="py-12 bg-stone-100">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-stone-100 via-stone-200 to-burgundy-100/50 rounded-2xl p-8 text-center border border-stone-300"
            >
              <Building2 className="w-12 h-12 text-burgundy-400 mx-auto mb-4" />
              <h3 className="text-stone-700 text-2xl font-bold mb-2">
                매너 강사를 모집합니다
              </h3>
              <p className="text-stone-500 mb-6">
                New Coupling 회원들에게 품격있는 매너를 알려주세요
              </p>
              <Button
                onClick={() => setShowAdInquiryModal(true)}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white font-bold"
              >
                강사 등록 문의
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      <FooterSection />

      {/* Content Detail Modal */}
      <AnimatePresence>
        {selectedContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4"
            onClick={() => setSelectedContent(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* === 미디어 영역 === */}
              <div className="relative bg-stone-100">
                {selectedContent.content_type === "video" && selectedContent.media_url ? (
                  <div className="aspect-video bg-stone-900">
                    <video
                      src={selectedContent.media_url.startsWith("http") ? selectedContent.media_url : `${API_URL}${selectedContent.media_url}`}
                      controls
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (selectedContent.media_url || selectedContent.thumbnail_url) ? (
                  <div className="bg-stone-50 max-h-[50vh] flex items-center justify-center">
                    <img
                      src={(() => {
                        const src = selectedContent.media_url || selectedContent.thumbnail_url;
                        return src.startsWith("http") ? src : `${API_URL}${src}`;
                      })()}
                      alt={selectedContent.title}
                      className="w-full max-h-[50vh] object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full flex items-center justify-center bg-gradient-to-br from-burgundy-500 to-burgundy-700">
                    <Users className="w-20 h-20 text-white/50" />
                  </div>
                )}

                <button
                  onClick={() => setSelectedContent(null)}
                  className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors shadow-lg"
                  data-testid="manner-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* === 본문 영역 === */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="flex items-start justify-between mb-4 gap-4">
                  <div className="flex-1 min-w-0">
                    <span className="text-burgundy-500 text-sm font-medium">
                      {categoryInfo[selectedContent.category]?.title || selectedContent.category}
                    </span>
                    <h2 className="text-2xl font-[720] text-stone-800 mt-1 leading-snug">
                      {selectedContent.title}
                    </h2>
                  </div>
                  <div className="flex items-center gap-4 text-stone-500 text-sm shrink-0 mt-1">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {selectedContent.views || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      {selectedContent.likes || 0}
                    </span>
                  </div>
                </div>

                {/* 요약 (description) */}
                {selectedContent.description ? (
                  <p className="text-stone-700 text-base leading-relaxed font-medium mb-4 pb-4 border-b border-stone-100">
                    {selectedContent.description}
                  </p>
                ) : null}

                {/* 본문 (content) — HTML 또는 plain text */}
                {selectedContent.content ? (
                  /<[a-z][\s\S]*>/i.test(selectedContent.content) ? (
                    <div
                      className="text-stone-700 leading-relaxed prose prose-stone max-w-none prose-headings:font-bold prose-headings:text-stone-800 prose-p:my-3 prose-li:my-1"
                      dangerouslySetInnerHTML={{ __html: selectedContent.content }}
                    />
                  ) : (
                    <p className="text-stone-700 whitespace-pre-wrap leading-relaxed">
                      {selectedContent.content}
                    </p>
                  )
                ) : !selectedContent.description ? (
                  <p className="text-stone-400 text-sm italic">상세 내용이 아직 준비되지 않았습니다.</p>
                ) : null}

                {selectedContent.tags && selectedContent.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6">
                    {selectedContent.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-stone-100 text-stone-600 text-sm rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl max-w-md w-full p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-2">
                프리미엄 콘텐츠
              </h3>
              <p className="text-stone-500 mb-6">
                이 콘텐츠는 정회원 이상만 이용 가능합니다.
                <br />
                지금 업그레이드하고 모든 매너 강의를 수강하세요!
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 py-3"
                >
                  취소
                </Button>
                <Button
                  onClick={() => navigate("/membership")}
                  className="flex-1 py-3 bg-burgundy-500 hover:bg-burgundy-600"
                >
                  멤버십 보기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ad Inquiry Modal */}
      <AnimatePresence>
        {showAdInquiryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => !adInquirySubmitting && setShowAdInquiryModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-br from-burgundy-500 to-burgundy-700 p-6 text-center">
                <button
                  onClick={() => !adInquirySubmitting && setShowAdInquiryModal(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">강사 등록 문의</h3>
                <p className="text-white/80 text-sm mt-1">New Coupling 매너 강사로 활동하세요</p>
              </div>

              {adInquirySuccess ? (
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <h4 className="text-xl font-bold text-stone-800 mb-2">
                    문의가 접수되었습니다!
                  </h4>
                  <p className="text-stone-500">
                    담당자가 검토 후 빠른 시일 내에 연락드리겠습니다.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleAdInquirySubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      강사/업체명 *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <input
                        type="text"
                        required
                        value={adInquiryForm.company_name}
                        onChange={(e) => setAdInquiryForm(prev => ({ ...prev, company_name: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all"
                        placeholder="이름 또는 업체명"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      전문 분야 *
                    </label>
                    <select
                      value={adInquiryForm.category}
                      onChange={(e) => setAdInquiryForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all"
                    >
                      {Object.entries(categoryInfo).map(([key, info]) => (
                        <option key={key} value={key}>{info.title}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      담당자명 *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <input
                        type="text"
                        required
                        value={adInquiryForm.contact_name}
                        onChange={(e) => setAdInquiryForm(prev => ({ ...prev, contact_name: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all"
                        placeholder="담당자 이름"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        연락처 *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          type="tel"
                          required
                          value={adInquiryForm.phone}
                          onChange={(e) => setAdInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all"
                          placeholder="010-0000-0000"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        이메일 *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          type="email"
                          required
                          value={adInquiryForm.email}
                          onChange={(e) => setAdInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all"
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      소개 및 문의 내용
                    </label>
                    <textarea
                      rows={3}
                      value={adInquiryForm.message}
                      onChange={(e) => setAdInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent transition-all resize-none"
                      placeholder="간단한 자기소개 및 경력을 작성해주세요"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={adInquirySubmitting}
                    className="w-full py-4 bg-burgundy-500 hover:bg-burgundy-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {adInquirySubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        접수 중...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        등록 문의하기
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-stone-400 mt-2">
                    * 접수 후 1-2 영업일 내 담당자가 연락드립니다
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
